"use client";

import React, { useMemo } from "react";
import Link from "next/link";
import { SectionHeader, CTAButton, StatsTable, EntityDisplay, type StatsTableColumn, type StatsTableRow } from "fansunited-sports-ui";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { StageTabs } from "@/components/ui/StageTabs";
import { resolveZone, buildLegend } from "@/utilities/standings/zones";
import type { FUSportsStandingsEntryWithNativeTeamId, StageStandings } from "@/utilities/data/types";
const MAX_COMPACT_ROWS = 20;
export interface StandingsCompactProps {
  /** Either a flat entries list (legacy / single-stage) or a list of stages. */
  standings: FUSportsStandingsEntryWithNativeTeamId[] | StageStandings[];
  showHeader?: boolean;
  /** Override the standings URL (e.g. for competition-specific links). Defaults to nav.standings(). */
  standingsUrl?: string;
  /** Maximum number of teams to show. Defaults to 20. */
  maxRows?: number;
  /** Show the trailing "Full table" link beneath the table. Defaults to true. */
  showFullTableLink?: boolean;
  /** Team IDs (native, e.g. `fb:t:8197`) whose rows should be visually highlighted. */
  highlightIds?: string[];
  /**
   * When true, render a legend explaining the colour-coded qualification /
   * relegation zones using `labels.standings.rules.*` for the names. Defaults
   * to false so the homepage widget stays compact unless opted in.
   */
  showZones?: boolean;
}
function isStageStandingsArray(s: FUSportsStandingsEntryWithNativeTeamId[] | StageStandings[]): s is StageStandings[] {
  return s.length > 0 && typeof (s[0] as StageStandings).stage !== "undefined";
}

// Card chrome (bg/border/rounding) is applied on the parent wrapper around
// <StageTabs> so the tab strip and the table share one continuous container.
const tableStyles = {
  container: "table-surface",
  borderRadius: "none" as const,
  shadow: "none" as const,
  typography: {
    cell: "text-sm font-medium",
    header: "text-xs uppercase font-bold text-muted-foreground"
  }
};

/**
 * StandingsCompact - Compact standings table showing top teams.
 * Uses StatsTable from fansunited-sports-ui.
 */
export const StandingsCompact: React.FC<StandingsCompactProps> = ({
  standings,
  showHeader = true,
  standingsUrl,
  maxRows = MAX_COMPACT_ROWS,
  showFullTableLink = true,
  highlightIds,
  showZones = false
}) => {
  const highlightSet = useMemo(() => new Set(highlightIds ?? []), [highlightIds]);
  const labels = useLabels();
  const nav = useNavigation();
  // Only resolve the full-table URL when we'll actually render the link.
  // `nav.standings()` throws when there's no competition in the URL builder
  // context (e.g. a standings block embedded inside a post body).
  const needsFullTableUrl = showHeader || showFullTableLink;
  let fullTableUrl: string | undefined;
  if (needsFullTableUrl) {
    try {
      fullTableUrl = standingsUrl ?? nav.standings();
    } catch {
      fullTableUrl = undefined;
    }
  }

  // Build columns for compact view: rank, team, played, points
  const columns: StatsTableColumn[] = useMemo(() => [{
    key: "rank",
    header: "#",
    align: "left",
    width: "xs"
  }, {
    key: "team",
    header: labels.standings.table.team,
    align: "left",
    width: "flex",
    render: v => {
      const t = v as {
        name: string;
        logo?: string;
        fansUnitedTeamId?: string | null;
      };
      const teamUrl = t.fansUnitedTeamId ? nav.team(t.fansUnitedTeamId) : "#";
      return <Link prefetch={false} href={teamUrl} className="text-foreground hover:opacity-80">
                            <EntityDisplay name={t.name} image={<TeamLogo src={t.logo} size="md" />} />
                        </Link>;
    }
  }, {
    key: "played",
    header: labels.standings.table.played,
    align: "center",
    width: "sm"
  }, {
    key: "points",
    header: labels.standings.table.points,
    align: "center",
    width: "sm",
    className: "font-bold"
  }], [labels, nav]);
  const stages: StageStandings[] = isStageStandingsArray(standings) ? standings : standings.length > 0 ? [{
    stage: {
      id: "single",
      name: ""
    } as StageStandings["stage"],
    standings: {
      entries: standings
    } as StageStandings["standings"]
  }] : [];
  if (stages.length === 0) return null;
  const tabs = stages.map(s => ({
    id: s.stage.id,
    label: s.stage.name || labels.standings.title
  }));
  const renderTable = (stageId: string) => {
    const found = stages.find(s_0 => s_0.stage.id === stageId);
    if (!found) return null;
    const allEntries = found.standings.entries;
    const visibleEntries = allEntries.length > maxRows ? allEntries.slice(0, maxRows) : allEntries;
    const rows: StatsTableRow[] = visibleEntries.map((entry, index) => {
      const teamId = entry.competitor.fansUnitedTeamId ?? entry.competitor.id;
      const zone = showZones ? resolveZone(entry.rules?.[0], labels.standings.rules) : undefined;
      return {
        id: entry.competitor.id,
        rank: entry.rank ?? index + 1,
        team: {
          name: entry.competitor.name,
          logo: entry.competitor.logo,
          fansUnitedTeamId: entry.competitor.fansUnitedTeamId
        },
        played: entry.stats?.played ?? 0,
        points: entry.stats?.points ?? 0,
        highlight: highlightSet.has(teamId),
        ...(zone ? {
          zone
        } : {})
      };
    });
    const legend = showZones ? buildLegend(visibleEntries.flatMap(e => e.rules ?? []), labels.standings.rules) : undefined;
    return <StatsTable columns={columns} rows={rows} styles={tableStyles} legend={legend && legend.length > 0 ? legend : undefined} showLegend={Boolean(legend && legend.length > 0)} />;
  };
  return <section>
            {showHeader && <SectionHeader entity={{
      name: labels.homePage.standings,
      styles: {
        typography: {
          name: "text-sm uppercase font-bold text-foreground"
        }
      }
    }} primaryCta={<CTAButton text={labels.homePage.fullTable} href={fullTableUrl} variant="custom" styles={{
      container: "text-sm font-medium text-accent hover:text-accent/80 border-none p-0"
    }} />} styles={{
      container: "mb-4"
    }} />}
            <div className="table-surface">
                <StageTabs tabs={tabs} renderBody={renderTable} />
            </div>
            {showFullTableLink && fullTableUrl && <div className="mt-3 text-right">
                    <Link prefetch={false} href={fullTableUrl} className="text-sm font-medium link-themed hover:underline">
                        {labels.homePage.fullTable}
                    </Link>
                </div>}
        </section>;
};